

















import { Component, Prop, Vue } from "vue-property-decorator";
import _ from "@/boot/lodash";
import {
  dateFormat,
  dateRangeValues,
  indexColor,
  getYAxisWithCurrency,
  getTooltipFormattedLabel
} from "@/components/app/global/stats/charts/utils";
import { IImport } from "@/models/imports";

type MRRChartDataType = {
  value: number;
  valueFormatted: string;
  date: string;
  [key: string]: any;
};

@Component({
  components: {
    chart: () => import("@/components/app/global/stats/charts/chart/index.vue")
  }
})
export default class MRRChart extends Vue {
  // Props
  @Prop({ required: true })
  public readonly dateFrom!: Date;

  @Prop({ default: null })
  public readonly importId!: IImport["id"];

  @Prop({ default: 0 })
  public readonly startColorIndex!: number;

  public get config() {
    return {
      normalized: true,
      parsing: { xAxisKey: "date", yAxisKey: "value" },
      plugins: { tooltip: { callbacks: { label: getTooltipFormattedLabel } } },
      scales: { y: { ticks: { callback: getYAxisWithCurrency } } }
    };
  }

  public mapToChartData({
    data,
    currencyCode,
    field,
    formatted = true
  }): MRRChartDataType[] {
    return this.$_.map(data, i => ({
      ...i[currencyCode],
      value: _.get(i, `${currencyCode}.${field}`, 0),
      valueFormatted: _.get(
        i,
        `${currencyCode}.${field}${formatted ? "_formatted" : ""}`,
        "0"
      )
    })).filter(i => !!i);
  }

  public fields = [
    { label: this.$t("_.mrr").toString(), field: "mrr" },
    { label: this.$t("_.client_average").toString(), field: "mrr_client_avg" },
    {
      label: this.$t("_.service_average").toString(),
      field: "mrr_service_avg"
    }
  ];

  public getChartData({ data, currencyCode, currency }) {
    const mappedStatData = data
      ? (this.fields.reduce((res, i) => {
          res[i.label] = this.mapToChartData({
            data,
            currencyCode,
            field: i.field
          });
          return res;
        }, {}) as { [key: string]: MRRChartDataType[] })
      : null;

    const statData = mappedStatData || { [this.$t("_.mrr").toString()]: [] };

    const result: any[] = this.$_.map(statData, (values, key) => {
      // Get all dates from result
      const existingDates = (values || []).map(i =>
        this.$moment(i.date, "YYYY-MM-DD").toDate()
      );

      // Find first and last date
      const rangeDates = [
        _.head(existingDates) as Date,
        _.last(existingDates) as Date
      ];

      // Get date format i.e MMM YYYY or MMMM
      const group = "month";
      const format = dateFormat(group, rangeDates);

      const mappedData = dateRangeValues(
        group,
        rangeDates,
        existingDates,
        statData[key] || [],
        (item, mDate) => ({
          // Mapped chart data
          date: mDate.format(format),
          isFutureDate: !mDate.isSameOrBefore(this.dateFrom, "day"),
          value: this.$_.get(item, "value", 0),
          valueFormatted: this.$_.get(item, "valueFormatted", "0")
        })
      );

      const color = indexColor({
        index:
          this.$_.indexOf(Object.keys(statData), key) + this.startColorIndex
      });

      const labels = mappedData.map(i => i.date);

      const futureDate = (ctx, value) => {
        return ctx.p1.$context.raw.isFutureDate ? value : undefined;
      };

      const futureDateColor = ctx =>
        ctx.p0.$context.raw.isFutureDate
          ? indexColor({
              index: ctx.p1.$context.datasetIndex + this.startColorIndex,
              alpha: 0.7
            }).borderColor
          : undefined;

      return {
        labels,
        dataset: {
          currency,
          label: key,
          borderColor: color.borderColor,
          backgroundColor: `rgba(225,255,255, 0.0)`,
          data: mappedData,
          mappedData, // injecting here to use in the tooltip function,
          segment: {
            borderDash: ctx => futureDate(ctx, [4, 4]),
            borderColor: futureDateColor
          }
        }
      };
    });

    return {
      labels: result[0].labels,
      datasets: result.map(i => i.dataset)
    };
  }
}
